import { defineComponent as _defineComponent } from 'vue'
import { abbreviateNumber } from '~/assets/helper/functions'
import { roundValue } from '~/functions/ghg'
import ProgressRing from '~/components/metrics/ProgressRing.vue'
import type { MetricAssignmentType } from '~/types/types'
import {
  AnnualAggregationMethod,
  MetricTheoryOfChangeRelation,
  MetricValueKind,
} from '~/types/types'
import { computed, getCurrentInstance, toRefs } from 'vue'

// Define props
type AggregationPeriodView = {
  period: string
  rawActual?: number | null
  rawTarget?: number | null
  rawPerformancePercent?: number | null
  accumulatedActual?: number | null
  accumulatedTarget?: number | null
  accumulatedPerformancePercent?: number | null
  isMostRecentActivePeriod?: boolean
}

type AggregationView = {
  calculationType?: string | null
  periodStart?: number | null
  periodEnd?: number | null
  summary?: {
    actual?: number | null
    target?: number | null
    performancePercent?: number | null
  } | null
  periods?: AggregationPeriodView[] | null
}

type PeriodRemark = {
  period: string
  comment: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AssignmentSection',
  props: {
    metricAssignment: null,
    impactName: null,
    showProgressBar: { type: Boolean },
    weightLabel: null,
    remark: null,
    targetCalculationMethod: null,
    statusChipLabel: null,
    statusChipColor: null,
    showUniqueTargetChip: { type: Boolean },
    compact: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  metricAssignment: MetricAssignmentType,
  impactName?: string | null,
  showProgressBar?: boolean,
  weightLabel?: string | null,
  remark?: string | null,
  targetCalculationMethod?: 'fiveYear' | 'latestValidPeriod',
  statusChipLabel?: string | null,
  statusChipColor?: string | null,
  showUniqueTargetChip?: boolean,
  compact?: boolean,
};



const { metricAssignment } = toRefs(props)

const TOC_RELATION_I18N_KEYS: Record<MetricTheoryOfChangeRelation, string> = {
  [MetricTheoryOfChangeRelation.SolutionsOutput]: 'tocRelationSolutions',
  [MetricTheoryOfChangeRelation.Outcome]: 'tocRelationOutcome',
  [MetricTheoryOfChangeRelation.ImpactLongTerm]: 'tocRelationImpact',
}

const { proxy } = getCurrentInstance()!

const tocRelationLabel = computed(() => {
  const relation = metricAssignment.value?.metric?.tocRelation
  if (!relation) return null
  const key = TOC_RELATION_I18N_KEYS[relation]
  return key ? String(proxy.$t(key)) : null
})

const targetParameters = computed(
  () => metricAssignment.value.completionStatus.targetParameters,
)

const aggregationView = computed<AggregationView | null>(() =>
  ((metricAssignment.value?.completionStatus as any)?.aggregationView || null) as AggregationView | null,
)

const isComparableType = computed(() => {
  const valueType = metricAssignment.value?.metric?.valueType
  return (
    valueType === MetricValueKind.Int ||
    valueType === MetricValueKind.Float ||
    valueType === MetricValueKind.Binary
  )
})

const periods = computed(() => {
  const apiPeriods = aggregationView.value?.periods
    ?.map((p) => p?.period)
    .filter((period): period is string => Boolean(period))
  if (apiPeriods && apiPeriods.length) return apiPeriods

  const configuredStart = metricAssignment.value?.completionStatus?.targetParameters?.startDate
  const configuredEnd = metricAssignment.value?.completionStatus?.targetParameters?.endDate
  if (
    configuredStart !== null &&
    configuredStart !== undefined &&
    configuredEnd !== null &&
    configuredEnd !== undefined &&
    configuredEnd >= configuredStart
  ) {
    return Array.from(
      { length: configuredEnd - configuredStart + 1 },
      (_, i) => String(configuredStart + i),
    )
  }

  const values = metricAssignment.value.metricValues || []
  const periodsWithTargets = values
    .filter(
      (metricValue) =>
        metricValue?.target !== null && metricValue?.target !== undefined,
    )
    .map((metricValue) => metricValue.period)
    .filter((period): period is string => !!period)

  const fallbackPeriods = values
    .map((metricValue) => metricValue.period)
    .filter((period): period is string => !!period)

  const uniquePeriods = Array.from(
    new Set(periodsWithTargets.length ? periodsWithTargets : fallbackPeriods),
  )
  return uniquePeriods.sort((a, b) => Number(a) - Number(b))
})

const effectiveCalculationType = computed<AnnualAggregationMethod | null>(() => {
  const apiCalcType = aggregationView.value?.calculationType
  if (apiCalcType === 'sum') return AnnualAggregationMethod.Sum
  if (apiCalcType === 'average') return AnnualAggregationMethod.Avg
  if (apiCalcType === 'most_recent_value') return AnnualAggregationMethod.MostRecent

  const assignmentType = metricAssignment.value?.customTargetCalculationType
  if (assignmentType) return assignmentType

  // Normalize GraphQL calculation type string values.
  const legacy = targetParameters.value?.calculationType
  if (legacy === 'sum') return AnnualAggregationMethod.Sum
  if (legacy === 'average') return AnnualAggregationMethod.Avg
  if (legacy === 'most_recent_value') return AnnualAggregationMethod.MostRecent
  return null
})

const isMostRecent = computed(
  () => effectiveCalculationType.value === AnnualAggregationMethod.MostRecent,
)

const targetCalculationMethod = computed(
  () => props.targetCalculationMethod || 'fiveYear',
)

const isRunningAggregate = computed(
  () =>
    isComparableType.value &&
    !isMostRecent.value &&
    (effectiveCalculationType.value === AnnualAggregationMethod.Avg ||
      effectiveCalculationType.value === AnnualAggregationMethod.Sum ||
      effectiveCalculationType.value === null),
)

const periodColumns = computed(() => periods.value)
const firstPeriod = computed(() => (periodColumns.value.length ? periodColumns.value[0] : null))
const finalPeriod = computed(() =>
  periodColumns.value.length ? periodColumns.value[periodColumns.value.length - 1] : null,
)
const rawComparableRowCount = computed(() => 3)

const activeMostRecentPeriod = computed(() => {
  const apiActivePeriod = aggregationView.value?.periods?.find((row) => row?.isMostRecentActivePeriod)?.period
  if (apiActivePeriod) return apiActivePeriod

  for (let i = periodColumns.value.length - 1; i >= 0; i -= 1) {
    const period = periodColumns.value[i]
    const row = rawRowsByPeriod.value[period]
    if (
      row &&
      (row.actual !== null || row.target !== null || row.percentageAchieved !== null)
    ) {
      return period
    }
  }
  return null
})

const latestValidAggregatePeriod = computed(() => {
  for (let i = periodColumns.value.length - 1; i >= 0; i -= 1) {
    const period = periodColumns.value[i]
    const pct = aggregateRowsByPeriod.value[period]?.percentageAchieved
    if (pct !== null && pct !== undefined) return period
  }
  return null
})

const selectedAggregateSummaryPeriod = computed(() => {
  if (targetCalculationMethod.value === 'latestValidPeriod') {
    return latestValidAggregatePeriod.value
  }
  return finalPeriod.value
})

function getPeriodMetricValue(period: string) {
  return (metricAssignment.value.metricValues || []).find((v) => v.period == period)
}

function toNumericValue(value: unknown): number | null {
  if (value === null || value === undefined) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

// Per-period rows come straight from the server aggregation view (single source
// of truth); the client no longer recomputes actuals/targets/percentages.
const rawRowsByPeriod = computed(() => {
  const values: Record<string, { actual: number | null, target: number | null, percentageAchieved: number | null }> = {}
  aggregationView.value?.periods?.forEach((row) => {
    values[row.period] = {
      actual: toNumericValue(row.rawActual),
      target: toNumericValue(row.rawTarget),
      percentageAchieved: toNumericValue(row.rawPerformancePercent),
    }
  })
  return values
})

const aggregateRowsByPeriod = computed(() => {
  if (!isRunningAggregate.value) return {}
  const values: Record<string, { actual: number | null, target: number | null, percentageAchieved: number | null }> = {}
  aggregationView.value?.periods?.forEach((row) => {
    values[row.period] = {
      actual: toNumericValue(row.accumulatedActual),
      target: toNumericValue(row.accumulatedTarget),
      percentageAchieved: toNumericValue(row.accumulatedPerformancePercent),
    }
  })
  return values
})

const showAggregateRows = computed(
  () => isComparableType.value && hasTargetValues(metricAssignment.value) && isRunningAggregate.value,
)

const metricTitle = computed(() => {
  return metricAssignment.value?.metricNameOverride || metricAssignment.value?.metric?.name || ''
})

const unitPillText = computed(() => {
  const unit = metricAssignment.value?.unitName || metricAssignment.value?.metric?.unitName || ''
  return unit || String(proxy?.$t?.('metaUnitFallback') || '-')
})

const periodRangeText = computed(() => {
  if (!periodColumns.value.length) return ''
  const start = periodColumns.value[0]
  if (targetCalculationMethod.value === 'latestValidPeriod') {
    const end = isMostRecent.value
      ? activeMostRecentPeriod.value
      : selectedAggregateSummaryPeriod.value
    if (end) {
      return `${start}-${end}`
    }
  }
  return `${start}-${periodColumns.value[periodColumns.value.length - 1]}`
})

const methodBadgeText = computed(() => {
  if (!periodRangeText.value) return aggregationMethodLabel.value
  if (isMostRecent.value) {
    return String(proxy?.$t?.('badgeMostRecentPeriod', { range: periodRangeText.value }))
  }
  if (effectiveCalculationType.value === AnnualAggregationMethod.Avg) {
    return String(proxy?.$t?.('badgeAveragePeriod', { range: periodRangeText.value }))
  }
  return String(proxy?.$t?.('badgeSumPeriod', { range: periodRangeText.value }))
})

const uniqueTargetWindowRange = computed(() => {
  const configuredStart = targetParameters.value?.startDate
  const configuredEnd = targetParameters.value?.endDate
  const start = configuredStart ?? (periodColumns.value[0] ? Number(periodColumns.value[0]) : null)
  const end = configuredEnd ?? (finalPeriod.value ? Number(finalPeriod.value) : null)
  if (start === null || end === null || !Number.isFinite(start) || !Number.isFinite(end)) {
    return null
  }
  return `${start}–${end}`
})

const uniqueTargetNumericValue = computed(() => {
  const period = finalPeriod.value
  if (period && isRunningAggregate.value) {
    const accumulatedTarget = aggregateRowsByPeriod.value[period]?.target
    if (accumulatedTarget !== null && accumulatedTarget !== undefined) {
      return accumulatedTarget
    }
  }
  return toNumericValue(targetParameters.value?.target)
})

const uniqueTargetWindowText = computed(() => {
  const range = uniqueTargetWindowRange.value
  if (!range) return null

  const isBinary = metricAssignment.value?.metric?.valueType === MetricValueKind.Binary
  if (isBinary && effectiveCalculationType.value === AnnualAggregationMethod.Sum) {
    return String(proxy?.$t?.('uniqueTargetWindowBinarySum', { range }))
  }
  if (effectiveCalculationType.value === AnnualAggregationMethod.Avg) {
    return String(proxy?.$t?.('uniqueTargetWindowAverage', { range }))
  }
  return String(proxy?.$t?.('uniqueTargetWindowSum', { range }))
})

const uniqueTargetChipText = computed(() => {
  if (!isComparableType.value || !isRunningAggregate.value) return null
  if (effectiveCalculationType.value === AnnualAggregationMethod.MostRecent) return null

  const value = uniqueTargetNumericValue.value
  const window = uniqueTargetWindowText.value
  if (value === null || value === undefined || !window) return null

  const formattedValue = abbreviateNumber(roundValue(value), getLocale())
  return String(proxy?.$t?.('uniqueTargetChip', { value: formattedValue, window }))
})

const showUniqueTargetChip = computed(() => props.showUniqueTargetChip === true)

const progressSummaryText = computed(() => {
  const actual = calculateActual(metricAssignment.value?.completionStatus)
  const target = calculateTarget(metricAssignment.value?.completionStatus)
  return String(proxy?.$t?.('summaryActualOfTarget', { actual, target, unit: unitPillText.value }))
})

const targetProgressPercentValue = computed(() => {
  const apiSummaryPerformance = toNumericValue(
    aggregationView.value?.summary?.performancePercent,
  )
  if (
    apiSummaryPerformance !== null &&
    targetCalculationMethod.value === 'fiveYear'
  ) {
    return Math.round(apiSummaryPerformance)
  }

  if (isMostRecent.value) {
    const activePeriod = activeMostRecentPeriod.value
    const mostRecentPercentage = activePeriod
      ? rawRowsByPeriod.value[activePeriod]?.percentageAchieved
      : null
    if (mostRecentPercentage !== null && mostRecentPercentage !== undefined) {
      return Math.round(mostRecentPercentage)
    }
  }

  if (isRunningAggregate.value && periodColumns.value.length > 0) {
    const summaryPeriod = selectedAggregateSummaryPeriod.value
    const aggregatedPercentage = summaryPeriod
      ? aggregateRowsByPeriod.value[summaryPeriod]?.percentageAchieved
      : null
    if (aggregatedPercentage !== null && aggregatedPercentage !== undefined) {
      return Math.round(aggregatedPercentage)
    }
  }

  const fallbackRate = Number(metricAssignment.value?.completionStatus?.completionRate ?? 0)
  if (!Number.isFinite(fallbackRate)) return 0
  return Math.round(fallbackRate * 100)
})

const targetProgressPercentage = computed(() => `${targetProgressPercentValue.value}%`)

function comparePeriods(a: string, b: string): number {
  const aNum = Number(a)
  const bNum = Number(b)
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum
  return a.localeCompare(b, undefined, { numeric: true })
}

const periodRemarks = computed<PeriodRemark[]>(() => {
  const values = metricAssignment.value?.metricValues ?? []
  return values
    .map((value) => ({
      period: value.period,
      comment: value.comment?.trim() || '',
    }))
    .filter((value) => value.comment.length > 0)
    .sort((a, b) => comparePeriods(a.period, b.period))
})

const aggregationMethodLabel = computed(() => {
  if (effectiveCalculationType.value === AnnualAggregationMethod.Avg) {
    return String(proxy?.$t?.('aggregationAverage') || 'Average')
  }
  if (effectiveCalculationType.value === AnnualAggregationMethod.MostRecent) {
    return String(proxy?.$t?.('aggregationMostRecent') || 'Most recent')
  }
  return String(proxy?.$t?.('aggregationSum') || 'Sum')
})

function getLocale(): string {
  // Use component-scoped i18n if available, fallback to English
  return (proxy as any)?.$i18n?.locale || 'en'
}

// Helper method for calculated actual/target values
function calculateActual(completionStatus: any): string | number {
  const currentLocale = getLocale()
  const apiSummaryActual = toNumericValue(aggregationView.value?.summary?.actual)
  if (apiSummaryActual !== null && targetCalculationMethod.value === 'fiveYear') {
    const roundedApiActual = roundValue(apiSummaryActual)
    return abbreviateNumber(roundedApiActual, currentLocale)
  }

  let summaryActual: number | null = null
  if (isMostRecent.value) {
    summaryActual = [...periodColumns.value]
      .reverse()
      .map((period) => rawRowsByPeriod.value[period]?.actual)
      .find((value) => value !== null && value !== undefined) ?? null
  } else if (isRunningAggregate.value && periodColumns.value.length > 0) {
    const summaryPeriod = selectedAggregateSummaryPeriod.value
    summaryActual = summaryPeriod
      ? aggregateRowsByPeriod.value[summaryPeriod]?.actual ?? null
      : null
  }

  const roundedActual = roundValue(summaryActual ?? completionStatus.actual)
  return abbreviateNumber(roundedActual, currentLocale)
}

function calculateTarget(completionStatus: any): string | number {
  const currentLocale = getLocale()
  const apiSummaryTarget = toNumericValue(aggregationView.value?.summary?.target)
  if (apiSummaryTarget !== null && targetCalculationMethod.value === 'fiveYear') {
    const roundedApiTarget = roundValue(apiSummaryTarget)
    return abbreviateNumber(roundedApiTarget, currentLocale)
  }

  let summaryTarget: number | null = null
  if (isMostRecent.value) {
    summaryTarget = [...periodColumns.value]
      .reverse()
      .map((period) => rawRowsByPeriod.value[period]?.target)
      .find((value) => value !== null && value !== undefined) ?? null
  } else if (isRunningAggregate.value && periodColumns.value.length > 0) {
    const summaryPeriod = selectedAggregateSummaryPeriod.value
    summaryTarget = summaryPeriod
      ? aggregateRowsByPeriod.value[summaryPeriod]?.target ?? null
      : null
  }

  const target = summaryTarget ?? completionStatus?.targetParameters?.target ?? 0
  const roundedTarget = roundValue(target)
  return abbreviateNumber(roundedTarget, currentLocale)
}

function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '⋯'
  const currentLocale = getLocale()
  return abbreviateNumber(value, currentLocale)
}

/** Full, un-abbreviated value for hover tooltips (software only; absent from print). */
function formatExactNumber(value: number | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined
  return new Intl.NumberFormat(getLocale()).format(value)
}

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '⋯'
  return `${Math.round(value)}%`
}

function rawCellClass(
  period: string,
  rowIndex: number,
  totalRows: number,
  emphasize = false,
  blueText = false,
): string {
  const classes = ['border-b', 'border-gray-300', 'px-2', 'py-1.5']
  if (rowIndex === 0) classes.push(showAggregateRows.value ? 'border-t' : 'border-t-0')
  if (period === firstPeriod.value) classes.push('border-l')
  if (period === finalPeriod.value) classes.push('border-r')

  if (rowIndex === 0 && period === firstPeriod.value) classes.push('rounded-tl-md')
  if (rowIndex === 0 && period === finalPeriod.value) classes.push('rounded-tr-md')
  if (rowIndex === totalRows - 1 && period === firstPeriod.value) classes.push('rounded-bl-md')
  if (rowIndex === totalRows - 1 && period === finalPeriod.value) classes.push('rounded-br-md')

  if (emphasize) classes.push('font-bold')
  if (blueText) classes.push('text-blue-600')

  return classes.join(' ')
}

function hasTargetValues(item: MetricAssignmentType): boolean {
  if (item?.completionStatus?.hasTarget) return true
  return (item?.metricValues ?? []).some(
    (metricValue) =>
      metricValue?.target !== null && metricValue?.target !== undefined,
  )
}

return { __sfc: true,props, metricAssignment, TOC_RELATION_I18N_KEYS, proxy, tocRelationLabel, targetParameters, aggregationView, isComparableType, periods, effectiveCalculationType, isMostRecent, targetCalculationMethod, isRunningAggregate, periodColumns, firstPeriod, finalPeriod, rawComparableRowCount, activeMostRecentPeriod, latestValidAggregatePeriod, selectedAggregateSummaryPeriod, getPeriodMetricValue, toNumericValue, rawRowsByPeriod, aggregateRowsByPeriod, showAggregateRows, metricTitle, unitPillText, periodRangeText, methodBadgeText, uniqueTargetWindowRange, uniqueTargetNumericValue, uniqueTargetWindowText, uniqueTargetChipText, showUniqueTargetChip, progressSummaryText, targetProgressPercentValue, targetProgressPercentage, comparePeriods, periodRemarks, aggregationMethodLabel, getLocale, calculateActual, calculateTarget, formatNumber, formatExactNumber, formatPercent, rawCellClass, hasTargetValues }
}

})